"use client";
import { getVacancies } from "@/app/actions";
import { Vacancy } from "@/app/network/apis/vacancy-api";
import { JobCard } from "@/components/common/job-card/job-card";
import ScreenLayout from "@/components/layout/screen-layout";
import { JobCardsSkeleton } from "@/components/ui/custom/shimmer";
import { Search, X } from "lucide-react";
import Image from "next/image";
import { useEffect, useMemo, useState } from "react";

export default function OpeningsTabsSectionOpeningsDevkrest() {
  const [vacancies, setVacancies] = useState<Vacancy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Filter vacancies based on search query
  const filteredVacancies = useMemo(() => {
    if (!searchQuery.trim()) return vacancies;

    const query = searchQuery.toLowerCase();
    return vacancies.filter(vacancy =>
      vacancy.title.toLowerCase().includes(query) ||
      vacancy.department.toLowerCase().includes(query) ||
      vacancy.location.toLowerCase().includes(query) ||
      vacancy.type.toLowerCase().includes(query) ||
      vacancy.description.toLowerCase().includes(query)
    );
  }, [vacancies, searchQuery]);

  const clearSearch = () => {
    setSearchQuery("");
  };

  useEffect(() => {
    const fetchVacancies = async () => {
      try {
        setLoading(true);
        const result = await getVacancies({ status: "1" });
        if (result.success) {
          setVacancies(result.vacancies);
        } else {
          setError(result.message || "Failed to fetch vacancies");
        }
      } catch (err) {
        setError("Something went wrong while fetching vacancies");
        console.error("Error fetching vacancies:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchVacancies();
  }, []);

  return (
    <ScreenLayout>
      <div className="max-w-5xl mx-auto">
        {loading ? (
          <div className="py-4">
            <div className="h-12 bg-gray-200 rounded-md w-full max-w-md mx-auto mb-8 animate-pulse"></div>
            <JobCardsSkeleton count={6} />
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <div className="flex items-center justify-center mb-2">
                <svg className="w-6 h-6 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <h3 className="text-lg font-semibold text-red-800">Error Loading Vacancies</h3>
              </div>
              <p className="text-red-600">{error}</p>
            </div>
          </div>
        ) : vacancies.length === 0 ? (
          <div className="py-12">
            <div className="max-w-2xl mx-auto p-6 text-center">
              <div className="mb-6 flex justify-center">
                <Image src="/images/openings/placeholder-openings.webp" alt="No openings" width={360} height={220} className="h-auto w-[360px] object-contain" />
              </div>
              <h3 className="text-3xl font-bold text-gray-900/80 mb-2">No Open Positions Available</h3>
              <p className="text-gray-600/80">We don't have any openings right now. Please check back soon.</p>
            </div>
          </div>
        ) : (
          <div className="pt-11 pb-28">
            {/* Search Bar */}
            <div className="mb-8">
              <div className="relative max-w-lg mx-auto">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search jobs by title, department, location..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
                />
                {searchQuery && (
                  <button
                    onClick={clearSearch}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    <X className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  </button>
                )}
              </div>
              {searchQuery && (
                <p className="text-center text-sm text-gray-600 mt-3">
                  {filteredVacancies.length} {filteredVacancies.length === 1 ? 'result' : 'results'} found for "{searchQuery}"
                </p>
              )}
            </div>

            {/* Jobs Grid */}
            {filteredVacancies.length === 0 && searchQuery ? (
              <div className="py-12">
                <div className="max-w-2xl mx-auto p-6 text-center">
                  <div className="mb-6 flex justify-center">
                    <Image src="/images/openings/placeholder-openings.webp" alt="No search results" width={320} height={200} className="h-auto w-[320px] object-contain" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900/80 mb-2">No jobs found</h3>
                  <p className="text-gray-600/80">Try searching with different keywords or clear your search to see all openings.</p>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {filteredVacancies.map((job) => (
                  <JobCard key={job.uuid} job={job} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </ScreenLayout>
  );
}
